import { MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiError } from '../../api/client';
import { EmptyState } from '../../components/EmptyState';
import { SidePanel } from '../../components/SidePanel';
import { useSubjectMutations, useSubjects } from '../../planner/api';
import type { Subject } from '../../planner/api';
import { iconFor, readableOn } from '../../planner/subjectStyle';
import PackageImport from './PackageImport';
import SubjectFieldsEditor from './SubjectFieldsEditor';
import { SubjectForm } from './SubjectForm';

function errorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) return 'Dit vak bestaat al.';
  if (error instanceof ApiError && error.status === 400) return 'Controleer de naam (1–40 tekens) en de kleur.';
  return 'Er ging iets mis. Probeer het opnieuw.';
}

/** Menu rows are touch targets: 44 px tall. */
const ITEM = 'min-h-11';

export default function SubjectsPanel() {
  const subjects = useSubjects();
  const { create, update, remove, reorder } = useSubjectMutations();
  const [adding, setAdding] = useState(false);
  const [showPackages, setShowPackages] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fieldsId, setFieldsId] = useState<string | null>(null);
  const error = create.error ?? update.error ?? remove.error ?? reorder.error;

  const move = (list: Subject[], index: number, delta: number) => {
    const ids = list.map((subject) => subject.id);
    const target = index + delta;
    const [item] = ids.splice(index, 1);
    if (item === undefined || target < 0 || target > ids.length) return;
    ids.splice(target, 0, item);
    reorder.mutate(ids);
  };

  return (
    <SidePanel title="Vakken" description="De vakken die je in het rooster kiest. Elk vak heeft een vaste kleur.">
      <div className="flex flex-wrap gap-2">
        <Button className="min-h-11" aria-expanded={adding} onClick={() => setAdding((value) => !value)}>
          <Plus /> Vak toevoegen
        </Button>
        <Button variant="outline" className="min-h-11" aria-expanded={showPackages} onClick={() => setShowPackages((value) => !value)}>
          Pakket importeren
        </Button>
      </div>

      {adding && (
        <SubjectForm
          submitLabel="Toevoegen"
          pending={create.isPending}
          onSubmit={(input, reset) =>
            create.mutate(input, {
              onSuccess: () => {
                reset();
                setAdding(false);
              },
            })
          }
          onCancel={() => setAdding(false)}
        />
      )}
      {showPackages && <PackageImport />}

      {error && (
        <p role="alert" className="text-sm">
          {errorText(error)}
        </p>
      )}

      {subjects.isPending ? (
        <p>Laden…</p>
      ) : subjects.isError ? (
        <p role="alert">De vakken konden niet worden geladen. Vernieuw de pagina.</p>
      ) : subjects.data.length === 0 ? (
        <EmptyState title="Nog geen vakken" note="Voeg je eerste vak toe. Elk vak krijgt een vaste kleur in het rooster." />
      ) : (
        <ul className="flex flex-col divide-y">
          {subjects.data.map((subject, index, list) => (
            <li key={subject.id} className="flex flex-col gap-2 py-2">
              {editingId === subject.id ? (
                <SubjectForm
                  initial={subject}
                  submitLabel="Opslaan"
                  pending={update.isPending}
                  onSubmit={(input) => update.mutate({ id: subject.id, ...input }, { onSuccess: () => setEditingId(null) })}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <span
                    className="flex min-h-11 flex-1 items-center gap-2 rounded-lg px-3 font-semibold"
                    style={{ background: subject.color, color: readableOn(subject.color) }}
                  >
                    <span aria-hidden="true" className="shrink-0">{iconFor(subject.icon)}</span>
                    <span className="min-w-0 break-words hyphens-auto">{subject.name}</span>
                    {subject.fields.length > 0 && <span className="ml-auto text-xs font-normal opacity-80">{subject.fields.length} {subject.fields.length === 1 ? 'veld' : 'velden'}</span>}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={`Menu ${subject.name}`} className="min-h-11 min-w-11">
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className={ITEM} onSelect={() => setEditingId(subject.id)}>
                        Bewerken
                      </DropdownMenuItem>
                      <DropdownMenuItem className={ITEM} onSelect={() => setFieldsId(fieldsId === subject.id ? null : subject.id)}>
                        Velden
                      </DropdownMenuItem>
                      <DropdownMenuItem className={ITEM} disabled={index === 0} onSelect={() => move(list, index, -1)}>
                        Omhoog
                      </DropdownMenuItem>
                      <DropdownMenuItem className={ITEM} disabled={index === list.length - 1} onSelect={() => move(list, index, 1)}>
                        Omlaag
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className={`${ITEM} text-[var(--danger)]`} onSelect={() => remove.mutate(subject.id)}>
                        Verwijderen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              {fieldsId === subject.id && editingId !== subject.id && (
                <SubjectFieldsEditor key={subject.id} subject={subject} onClose={() => setFieldsId(null)} />
              )}
            </li>
          ))}
        </ul>
      )}
    </SidePanel>
  );
}
